import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import {
  type EmailAddress,
  type TimestampUTC,
  type UUID,
} from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import {
  EmailSchema,
  ValidUuidOrNotSetSchema,
  withoutObjectKeysWhereValueIs,
} from '@vestigas/utils';
import { log } from '~/utils';

import { queryKeysInvoiceAiModel } from './queryKeys';

const zodCreateInvoiceAiModelSenderPayloadSchema = z
  .object({
    id: ValidUuidOrNotSetSchema,
    companyDomain: z
      .union([z.string().min(3).includes('.'), z.literal('')])
      .nullish(),
    senderAddress: z.union([EmailSchema, z.literal('')]).nullish(),
  })
  .refine(
    ({ companyDomain, senderAddress }) => {
      return [senderAddress, companyDomain].some(Boolean);
    },
    { error: 'Sender address or company domain must be provided' },
  )
  .transform((data) => withoutObjectKeysWhereValueIs(data, [null, undefined]));

type CreateInvoiceAiModelSenderPayload = z.infer<
  typeof zodCreateInvoiceAiModelSenderPayloadSchema
>;

const zodCreateInvoiceAiModelSenderEndpointSchema = z.tuple([
  z.literal('vadmin'),
  z.literal('invoice'),
  z.literal('ai_model_settings'),
  z.uuid(),
  z.literal('sender'),
]);

type CreateInvoiceAiModelSenderResponse = {
  companyDomain: string | undefined;
  createdOn: TimestampUTC;
  id: UUID;
  modifiedOn: TimestampUTC | undefined;
  senderAddress: EmailAddress | undefined;
};

/**
 * Creates an invoice AI model sender.
 *
 * @param invoiceAiModelSettingsId - The ID of the invoice AI model settings.
 * @param senderData - An object containing the sender data to be created.
 * @returns - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/create_invoice_ai_model_sender_vadmin_invoice_ai_model_settings__model_id__sender_post
 */
export const createInvoiceAiModelSender = async ({
  invoiceAiModelSettingsId,
  senderData,
}: {
  invoiceAiModelSettingsId: UUID;
  senderData: CreateInvoiceAiModelSenderPayload;
}) => {
  try {
    const response = await vestigasApi
      .post(
        ENDPOINT.INVOICE_AI_MODEL.SETTINGS.CREATE_SENDER(
          invoiceAiModelSettingsId,
        ),
        {
          json: senderData,
          validate: {
            body: zodCreateInvoiceAiModelSenderPayloadSchema,
            pathSegments: zodCreateInvoiceAiModelSenderEndpointSchema,
          },
        },
      )
      .json<CreateInvoiceAiModelSenderResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error creating invoice AI model sender',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error; // re-throw error so it can be handled higher up in the callstack.
  }
};

/**
 * Custom hook for creating invoice AI model sender using mutations.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/create_invoice_ai_model_sender_vadmin_invoice_ai_model_settings__model_id__sender_post
 */
export const useMutationCreateInvoiceAiModelSender = (
  options?: UseMutationOptions<
    CreateInvoiceAiModelSenderResponse,
    unknown,
    {
      invoiceAiModelSettingsId: UUID;
      senderData: CreateInvoiceAiModelSenderPayload;
    }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      invoiceAiModelSettingsId,
      senderData,
    }: {
      invoiceAiModelSettingsId: UUID;
      senderData: CreateInvoiceAiModelSenderPayload;
    }) =>
      createInvoiceAiModelSender({
        invoiceAiModelSettingsId,
        senderData,
      }),
    onSettled(_data, _error, { invoiceAiModelSettingsId }) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceAiModel.getSettings(invoiceAiModelSettingsId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceAiModel.getAllSettings({}),
      });
    },
    ...options,
  });
};
